/**
 * Singleton class for adding styles dynamically.
 * @author Branislav Bogdanovic
 */
import _ from 'lodash';

export class DynamicStyle {
  private styles: { [key: string]: CSSStyleRule } = {};
  private originalStyles: {
    [key: string]: { [key: string]: any } | undefined;
  } = {};

  /**
   * Removes all styles when theme is changed.
   */
  public emptyStyles = () => {
    this.styles = {};
    this.originalStyles = {};
  };

  /**
   * Finds first style to match passed selector.
   * @param selector Class full name (e.g .k-animation-container-shown, .k-animation-container > .k-popup)
   * @param stylesToChange Expect parameter to be in camelCase notation. (e.g {zIndex: 10})
   */
  public findStyle = (selector: string, stylesToChange: { [key: string]: any } = {}) => {
    // <debug>
    if (_.isEmpty(stylesToChange)) {
      throw new Error('There is no need to call "findStyle" without passing styles to change!');
    }
    // </debug>
    if (!this.styles[selector]) {
      const regex = new RegExp(selector, 'g');
      let rules: CSSStyleRule[] = [];
      let i;
      if (document.styleSheets) {
        for (i = 0; i < document.styleSheets.length; i++) {
          if (document.styleSheets[i].href) {
            rules = (document.styleSheets[i] as any).cssRules;
            break;
          }
        }
      }
      let rule: CSSStyleRule;
      for (i = 0; i < rules.length; i++) {
        rule = rules[i];
        if (rule.selectorText && rule.selectorText.match(regex)) {
          this.styles[selector] = rule;
          break;
        }
      }
    }
    // <debug>
    if (!this.styles[selector]) {
      throw new Error(`There is no match for selector: "${selector}"\nPlease pass valid selector in order to dynamically find styles.`);
    }
    // </debug>
    let styleName;
    const properties: string[] = Object.keys(stylesToChange);
    if (!this.originalStyles[selector]) {
      this.originalStyles[selector] = {};
    }
    for (let j = 0; j < properties.length; j++) {
      styleName = properties[j];
      // <debug>
      // @ts-ignore
      if (_.isUndefined(this.styles[selector].style[styleName])) {
        console.error(`Property "${styleName}" doesn't exists. Please remove property because it takes no effects.`);
        continue;
      }
      // </debug>
      // @ts-ignore
      _.set(this.originalStyles[selector], styleName, _.get(this.styles[selector].style, styleName));
      _.set(this.styles[selector].style, styleName, stylesToChange[styleName]);
    }
  };

  /**
   * Undoes passed style to its previous value.
   * @param selector
   */
  public resetStyle = (selector: string) => {
    if (this.styles[selector]) {
      const originalStyle: any = _.get(this.originalStyles, selector);
      const properties: string[] = Object.keys(originalStyle);
      let styleName;
      for (let i = 0; i < properties.length; i++) {
        styleName = properties[i];
        _.set(this.styles[selector].style, styleName, originalStyle[styleName]);
      }
    }
    // <debug>
    else {
      // throw new Error(`Can't reset non existing selector "${selector}".`);
      console.error(`Can't reset non existing selector "${selector}".`);
    }
    // </debug>
  };

  /**
   * Clean up selector styles after component is unmounted.
   * @param selector
   */
  public remove = (selector: string): void => {
    this.originalStyles[selector] = undefined;
    _.set(this.styles, selector, undefined);
  };
}

const DynamicStyleManager = new DynamicStyle();
// <debug>
// @ts-ignore
window.DynamicStyleManager = DynamicStyleManager;
// </debug>
export default DynamicStyleManager;
